import axios from 'axios';
import swal from 'sweetalert2';
import { ROOT_URL, getAuthHeaders, isUnauthorizedError, S3_BUCKET, S3_PREFIX_OBSERVATION_PROCESSES, uploadFileViaS3 } from './constants';

// Actualiza en el estado global.
export const GET_VIDEO = 'GET_VIDEO';
export const GET_VIDEOS = 'GET_VIDEOS';
export const CREATE_VIDEO = 'CREATE_VIDEO';
export const UPDATE_PERMISSION = 'UPDATE_PERMISSION';
export const GET_PROCESSESVIDEOS = 'GET_PROCESSESVIDEOS';
export const DELETE_VIDEO = 'DELETE_VIDEO';
export const EDIT_VIDEO = 'EDIT_VIDEO';
export const GET_MULTIMEDIA_URL = 'GET_MULTIMEDIA_URL';
export const ASOCIATE_PROCESSES_TO_VIDEO = 'ASOCIATE_PROCESSES_TO_VIDEO';

/**
 * Sube un archivo de video o proceso de observación a S3.
 *
 * @export uploadObservationProcessFile
 */
export async function uploadObservationProcessFile(file, filename, onUploadProgress) {
  try {
    return await uploadFileViaS3(
      '/videos/upload_url',
      file,
      filename,
      S3_BUCKET,
      S3_PREFIX_OBSERVATION_PROCESSES,
      undefined,
      onUploadProgress
    );
  } catch (error) {
    console.error('Error subiendo archivo de video/proceso:', error);
    swal({
      type: 'error',
      title: 'Error',
      text: 'No se pudo subir el archivo. Por favor intente nuevamente.',
    });
    throw error;
  }
}

function buildObservationProcessS3Query(extra = {}) {
  const query = new URLSearchParams({
    bucket: S3_BUCKET,
    prefix: S3_PREFIX_OBSERVATION_PROCESSES,
  });
  Object.entries(extra).forEach(([key, value]) => {
    if (value) {
      query.set(key, value);
    }
  });
  return query.toString();
}

/**
 * Renombra archivos de video/instrucciones en S3 tras cambiar el título.
 *
 * @export renameVideoS3Files
 */
export async function renameVideoS3Files(id, { oldVideo, newVideo, oldInstructions, newInstructions } = {}) {
  const query = buildObservationProcessS3Query({
    old_video: oldVideo,
    new_video: newVideo,
    old_instructions: oldInstructions,
    new_instructions: newInstructions,
  });
  console.log('query', query);
  return axios.patch(`${ROOT_URL}/videos/${id}/rename_s3_files?${query}`, {}, getAuthHeaders());

}

/**
 * Elimina un archivo del bucket de procesos/videos en S3.
 *
 * @export deleteS3StorageFile
 */
export async function deleteS3StorageFile(filename) {
  const query = buildObservationProcessS3Query({ filename });
  return axios.delete(`${ROOT_URL}/videos/delete_storage_file?${query}`, getAuthHeaders());
}


/**
 * Crea un video en el sistema con los datos dados en el parámetro video.
 *
 * @export createVideo
 * @param {*} video : Información del nuevo video a crear
 * @param {*} [successCallback=() => {}] : Función a llamar en caso de petición exitosa.
 * @param {*} [errorCallback=(error) => {}] : Función a llamar en caso de una petición con error.
 *                                           La función debe recibir por parámetro el error generado.
 * @returns Función de manejo de error o éxito de la petición.
 */
 export function createVideo(video, successCallback = () => {}, errorCallback = (error) => {}) {
    const request = axios.post(`${ROOT_URL}/videos`, {video: video}, getAuthHeaders());
  
    return (dispatch) => {
      request.then(({data}) => {
        dispatch({
          type: CREATE_VIDEO,
          payload: data
        });
        successCallback(data.video.video);
      }).catch((error) => {
        if (!isUnauthorizedError(error, dispatch)) {
          errorCallback(error);
        }
      });
    }
  }

/**
 * Obtener el video con el id dado.
 *
 * @export getVideo
 * @param {*} id : El id de la respuesta
 * @param {*} email : El email del observador para hacer la petición a solo los que puede ver
 * @returns Función de manejo de error o éxito de la petición.
 */
 export function getVideo(id, email = undefined, successCallback = ()=> {}, errorCallback=(error)=>{}) {
  let request;
  if (email) {
    request = axios.get(`${ROOT_URL}/videos/toObserver?id=${id}&email=${email}`, getAuthHeaders());
  } else {
    request = axios.get(`${ROOT_URL}/videos/${id}`, getAuthHeaders());
  }

  return (dispatch) => {
    request.then(({data}) => {
      dispatch({
        type: GET_VIDEO,
        payload: data
      });
      successCallback(data);
    }).catch((error) => {
      if (!isUnauthorizedError(error, dispatch)) {
        console.log('Error fetching the video', error);
        errorCallback(error);
      }
    });
  }
}

/**
 * Obtener la lista de videos asociados a los procesos del gestor que los consulta.
 *
 * @export getVideos
 * @returns Función de manejo de error o éxito de la petición.
 */
 export function getVideos({pagination = '', search = ''}, successCallback = ()=> {}, errorCallback=(error)=>{}) {
  const searchQuery = search ? `&search%5Bquery%5D=${search.query}&search%5Bvalue%5D=${search.value}` : ''
  const request = axios.get(`${ROOT_URL}/videos${pagination}${searchQuery}`, getAuthHeaders());
  
  return (dispatch) => {
    request.then(({data}) => {
      dispatch({
        type: GET_VIDEOS,
        payload: search ? {...data, search} : data
      });
      successCallback();
    }).catch((error) => {
      if (!isUnauthorizedError(error, dispatch)) {
        console.log('Error fetching the video', error);
        errorCallback(error);
      }
    });
  }
}

/**
 * Actualizar si un video se marca como visto para restringir su acceso
 *
 * @export updatePermission
 * @returns Función de manejo de error o éxito de la petición.
 */
export function updatePermission(id, data, successCallback = ()=>{}, errorCallback = ()=>{}) {
    const request = axios.put(`${ROOT_URL}/permissions/${id}/byObserver`, data, getAuthHeaders());
  return (dispatch) => {
    request.then(({data}) => {
      dispatch({
        type: UPDATE_PERMISSION,
        payload: data
      });
      successCallback();
    }).catch((error) => {
      if (!isUnauthorizedError(error, dispatch)) {
        console.log('Error fetching the video', error);
        errorCallback();
      }
    });
  }
}


/**
 * Obtiene los videos asociados al proceso consultado
 *
 * @export getprocessVideos
 * @param {*} process_id : El id del proceso que se desea consultar sus videos
 * @returns Función de manejo de error o éxito de la petición.
 */
export function getprocessVideos(pagination, {processId='', isObserver=false}, successCallback = () => {}, errorCallback = (error) => {}) {
  const request = axios.get(`${ROOT_URL}/videos${pagination}&[process_id]=${processId}&[isObserver]=${isObserver}`, getAuthHeaders());
  // const request = axios.get(`${ROOT_URL}/videos?id=${processId}`, getAuthHeaders());

  return (dispatch) => {
    request.then(({data}) => {
      dispatch({
        type: GET_PROCESSESVIDEOS,
        payload: data
      });
      successCallback();
    }).catch((error) => {
      if (!isUnauthorizedError(error, dispatch)) {
        errorCallback(error);
      }
    });
  }
}

/**
 * Eliminar un video según el id dado por parámetro.
 *
 * @export deleteVideo
 * @param {*} id : EL id del video
 * @param {*} [successCallback=() => {}] : Función a llamar en caso de petición exitosa.
 * @param {*} [errorCallback=(error) => {}] : Función a llamar en caso de una petición con error.
 *                                           La función debe recibir por parámetro el error generado.
 * @returns Función de manejo de error o éxito de la petición.
 */
 export function deleteVideo(id, successCallback = () => {}, errorCallback = (error) => {}) {
  const request = axios.delete(
    `${ROOT_URL}/videos/${id}/delete_video?bucket=${encodeURIComponent(S3_BUCKET)}&prefix=${encodeURIComponent(S3_PREFIX_OBSERVATION_PROCESSES)}`,
    getAuthHeaders()
  );
  
  return (dispatch) => {
    request.then(() => {
      dispatch({
        type: DELETE_VIDEO,
        payload: id
      });
      successCallback();
    }).catch((error) => {
      console.log(error)
      if (!isUnauthorizedError(error, dispatch)) {
        errorCallback(error);
      }
    });
  }
}

  /**
 * editar un video
 *
 * @export updateVideo
 * @param {*} [successCallback=() => {}] : Función a llamar en caso de petición exitosa.
 * @param {*} [errorCallback=(error) => {}] : Función a llamar en caso de una petición con error.
 *                                           La función debe recibir por parámetro el error generado.
 * @returns Función de manejo de error o éxito de la petición.
 */
 export function updateVideo(id, video, successCallback = () => {}, errorCallback = () => {}) {

  const request = axios.patch( `${ROOT_URL}/videos/${id}`, { video: video }, getAuthHeaders());

  return (dispatch) => {
    request
      .then(({ data }) => {
        dispatch({
          type: EDIT_VIDEO,
          payload: data,
        });
        successCallback(data.video.video);
      })
      .catch((error) => {
        if (!isUnauthorizedError(error, dispatch)) {
          console.log(error);
        }
        errorCallback(error);

      });
  };
}

/**
 * Obtener el enlace multimedia del video con el id dado.
 *
 * @export getMultimediaUrl
 * @param {*} id : El id del video
 * @returns Función de manejo de error o éxito de la petición.
 */
 export function getMultimediaUrl(id, ip, callBack= ()=>{}) {
  const request = axios.get(`${ROOT_URL}/videos/${id}/multimedia_url?bucket=${S3_BUCKET}&prefix=${S3_PREFIX_OBSERVATION_PROCESSES}&sip=${ip}`, getAuthHeaders());

  return (dispatch) => {
    request.then(({data}) => {
      dispatch({
        type: GET_MULTIMEDIA_URL,
        payload: data
      });
      callBack(data);
    }).catch((error) => {
      if (!isUnauthorizedError(error, dispatch)) {
        console.log('Error fetching the answer', error);
      }
    });
  }
}

  /**
 * asociar procesos al video
 *
 * @export asociate
 * @param {*} [successCallback=() => {}] : Función a llamar en caso de petición exitosa.
 * @param {*} [errorCallback=(error) => {}] : Función a llamar en caso de una petición con error.
 *                                           La función debe recibir por parámetro el error generado.
 * @returns Función de manejo de error o éxito de la petición.
 */
  export function asociate(id, processSelecteds) {
    const request = axios.post(`${ROOT_URL}/videos/asociate`,{ id, data: processSelecteds } ,getAuthHeaders());
  
    return (dispatch) => {
      request
        .then(({ data }) => {
          dispatch({
            type: ASOCIATE_PROCESSES_TO_VIDEO,
            payload: data,
          });
        })
        .catch((error) => {
          if (!isUnauthorizedError(error, dispatch)) {
            console.log(error);
          }
        });
    };
  }
  
